import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { format, isAfter } from 'date-fns';
import commonFunctions from '../../utils/commonFunctions';
import timeicon from '../../assets/icons/message-manager/time.svg'
import './TimePicker.css';

// Texts for localization/multi-language support
const TEXT = {
  placeholder: 'HH:MM AM/PM',
  labelHours: 'Hours',
  labelHoursRemark: 'Hours (01-12)',
  labelMinutes: 'Minutes',
  labelPeriod: 'AM/PM',
  labelAM: 'AM',
  labelPM: 'PM',
  buttonNow: 'Now',
  tooltipNowDisabled: 'Current time is after maximum allowed time',
  prefixMin: 'Min:',
  prefixMax: 'Max:',
  noteCustomMinute: 'Custom minute: {minutes} (00-59 allowed)',
};

const TimePicker = ({
  label,
  className = '',
  placeholder = TEXT.placeholder,
  value = '',
  minuteInterval = 15,
  onChange,
  minTime,
  maxTime,
  disabled = false,
  valid = true,
  layout = 'grid' // 'grid' or 'vertical-columns'
}) => {
  // Options for minutes and hours
  // Memoize hour options (01-12)
  const hourOptions = useMemo(() => Array.from({ length: 12 }, (_, i) => (i === 0 ? 12 : i).toString().padStart(2, '0')), []);
  // Memoize minute options based on minuteInterval
  const minuteOptions = useMemo(() => Array.from({ length: 60 / minuteInterval }, (_, i) => String(i * minuteInterval).padStart(2, '0')), []);

  // State for dropdown, input, and selection
  const [isVerticalSingleColumn] = useState(true); // Just for vertical-columns layout, true for single column, false for two columns
  const [isOpen, setIsOpen] = useState(false);
  const [isPeriodToggle, setIsPeriodToggle] = useState(false);
  const [inputValue, setInputValue] = useState(value ? format(value, 'hh:mm aa') : '');
  const [hours, setHours] = useState('12');
  const [minutes, setMinutes] = useState('00');
  const [period, setPeriod] = useState('AM');
  const [isTyping, setIsTyping] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [isNowDisabled, setIsNowDisabled] = useState(false); // State for Now button disabled status
  
  // Refs for DOM elements
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Check dropdown position (top or bottom) based on available space
  const checkDropdownPosition = useCallback(() => {
    if (!inputRef.current || !dropdownRef.current) return;

    const inputRect = inputRef.current.getBoundingClientRect();
    const dropdownHeight = layout === 'vertical-columns' ? 360 : 420;
    const spaceBelow = window.innerHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }
  }, [layout]);

  // Parse a time string into hours, minutes, and period
  const parseTimeString = useCallback((timeStr) => {
    const timeRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?$/i;
    const match = timeStr.trim().match(timeRegex);
    if (!match) return null;
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3] ? match[3].toUpperCase() : 'AM';
    if (hours < 1 || hours > 12) return null;
    const minutesNum = parseInt(minutes, 10);
    if (minutesNum < 0 || minutesNum > 59) return null;
    return {
      hours: hours.toString().padStart(2, '0'),
      minutes,
      period
    };
  }, []);

  // Format hours, minutes, and period into a time string
  const formatTime = useCallback((h, m, p) => {
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')} ${p}`;
  }, []);

  // Convert a time string to total minutes since midnight
  const timeToMinutes = useCallback((timeStr) => {
    if (!timeStr) return null;
    const parsed = parseTimeString(timeStr);
    if (!parsed) return null;
    let hour = parseInt(parsed.hours, 10);
    const minute = parseInt(parsed.minutes, 10);
    const isPM = parsed.period === 'PM';
    if (isPM && hour !== 12) {
      hour += 12;
    } else if (!isPM && hour === 12) {
      hour = 0;
    }
    return hour * 60 + minute;
  }, [parseTimeString]);

  // Find the closest valid time within minTime and maxTime
  const getClosestValidTime = useCallback((timeStr) => {
    let closestTime = timeStr;
    const currentMinutes = timeToMinutes(timeStr);
    if (minTime) {
      const minMinutes = timeToMinutes(format(minTime, 'hh:mm aa'));
      if (currentMinutes !== null && minMinutes !== null && currentMinutes < minMinutes) {
        closestTime = format(minTime, 'hh:mm aa');
      }
    }
    if (maxTime) {
      const maxMinutes = timeToMinutes(format(maxTime, 'hh:mm aa'));
      if (currentMinutes !== null && maxMinutes !== null && currentMinutes > maxMinutes) {
        closestTime = format(maxTime, 'hh:mm aa');
      }
    }
    return closestTime;
  }, [minTime, maxTime, timeToMinutes]);

  // Determine if a specific hour/minute/period option should be disabled
  const isOptionDisabled = useCallback((hour, minute, period) => {
    const currentTimeStr = `${hour}:${minute} ${period}`;
    const currentMinutes = timeToMinutes(currentTimeStr);
    if (currentMinutes === null) return false;
    let minMinutes = null;
    let maxMinutes = null;
    if (minTime) {
      minMinutes = timeToMinutes(format(minTime, 'hh:mm aa'));
    }
    if (maxTime) {
      maxMinutes = timeToMinutes(format(maxTime, 'hh:mm aa'));
    }
    if (minMinutes !== null && currentMinutes < minMinutes) {
      return true;
    }
    if (maxMinutes !== null && currentMinutes > maxMinutes) {
      return true;
    }
    return false;
  }, [minTime, maxTime, timeToMinutes]);

  // Determine if a specific hour should be disabled for the current period
  const isHourDisabled = useCallback((hour) => {
    return minuteOptions.every(minute => isOptionDisabled(hour, minute, period));
  }, [minuteOptions, isOptionDisabled, period]);

  // Determine if a period (AM/PM) should be disabled
  const isPeriodDisabled = useCallback((periodToCheck) => {
    return !minuteOptions.some(minute => !isOptionDisabled(hours, minute, periodToCheck));
  }, [minuteOptions, hours, isOptionDisabled]);

  // Get the list of enabled hour options
  const getFilteredHourOptions = useCallback(() => {
    if (!minTime && !maxTime) return hourOptions;
    return hourOptions.filter(hour => !isHourDisabled(hour));
  }, [minTime, maxTime, hourOptions, isHourDisabled]);

  // Get the list of enabled minute options for the current hour and period
  const getFilteredMinuteOptions = useCallback(() => {
    if (!minTime && !maxTime) return minuteOptions;
    return minuteOptions.filter(minute => !isOptionDisabled(hours, minute, period));
  }, [minTime, maxTime, minuteOptions, hours, period, isOptionDisabled]);

  // Validate if a given value is within minTime and maxTime
  const isValueValid = useCallback((value) => {
    if (!minTime && !maxTime) return true;
    const currentMinutes = timeToMinutes(value);
    if (currentMinutes === null) return false;
    let minMinutes = null;
    let maxMinutes = null;
    if (minTime) {
      minMinutes = timeToMinutes(format(minTime, 'hh:mm aa'));
    }
    if (maxTime) {
      maxMinutes = timeToMinutes(format(maxTime, 'hh:mm aa'));
    }
    if (minMinutes !== null && currentMinutes < minMinutes) {
      return false;
    }
    if (maxMinutes !== null && currentMinutes > maxMinutes) {
      return false;
    }
    return true;
  }, [minTime, maxTime, timeToMinutes]);

  // Check if Now button should be disabled based on current time and maxTime
  const checkNowButtonDisabled = useCallback(() => {
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    
    // Create a Date object for current time
    const currentDate = new Date();
    currentDate.setHours(currentHours);
    currentDate.setMinutes(currentMinutes);
    currentDate.setSeconds(0);
    currentDate.setMilliseconds(0);
    
    try {
      let minTimeDate = null;
      let maxTimeDate = null;
      
      // Parse minTime if provided
      if (minTime) {
        if (minTime instanceof Date) {
          minTimeDate = new Date();
          minTimeDate.setHours(minTime.getHours());
          minTimeDate.setMinutes(minTime.getMinutes());
          minTimeDate.setSeconds(0);
          minTimeDate.setMilliseconds(0);
        } else if (typeof minTime === 'string') {
          const parsedMin = parseTimeString(minTime);
          if (parsedMin) {
            minTimeDate = new Date();
            let minHours = parseInt(parsedMin.hours, 10);
            if (parsedMin.period === 'PM' && minHours !== 12) {
              minHours += 12;
            } else if (parsedMin.period === 'AM' && minHours === 12) {
              minHours = 0;
            }
            minTimeDate.setHours(minHours);
            minTimeDate.setMinutes(parseInt(parsedMin.minutes, 10));
            minTimeDate.setSeconds(0);
            minTimeDate.setMilliseconds(0);
          }
        }
      }
      
      // Parse maxTime if provided
      if (maxTime) {
        if (maxTime instanceof Date) {
          maxTimeDate = new Date();
          maxTimeDate.setHours(maxTime.getHours());
          maxTimeDate.setMinutes(maxTime.getMinutes());
          maxTimeDate.setSeconds(0);
          maxTimeDate.setMilliseconds(0);
        } else if (typeof maxTime === 'string') {
          const parsedMax = parseTimeString(maxTime);
          if (parsedMax) {
            maxTimeDate = new Date();
            let maxHours = parseInt(parsedMax.hours, 10);
            if (parsedMax.period === 'PM' && maxHours !== 12) {
              maxHours += 12;
            } else if (parsedMax.period === 'AM' && maxHours === 12) {
              maxHours = 0;
            }
            maxTimeDate.setHours(maxHours);
            maxTimeDate.setMinutes(parseInt(parsedMax.minutes, 10));
            maxTimeDate.setSeconds(0);
            maxTimeDate.setMilliseconds(0);
          }
        }
      }
      
      // Check if current time is before minTime
      let isBeforeMinTime = false;
      if (minTimeDate) {
        isBeforeMinTime = isAfter(minTimeDate, currentDate);
      }
      
      // Check if current time is after maxTime
      let isAfterMaxTime = false;
      if (maxTimeDate) {
        isAfterMaxTime = isAfter(currentDate, maxTimeDate);
      }
      
      // Now button should be disabled if:
      // 1. Current time is before minTime, OR
      // 2. Current time is after maxTime
      const disabled = isBeforeMinTime || isAfterMaxTime;
      setIsNowDisabled(disabled);
      return disabled;
    } catch (error) {
      console.error('Error comparing times:', error);
      setIsNowDisabled(false);
      return false;
    }
  }, [minTime, maxTime, parseTimeString]);

  // Update Now button disabled status when maxTime changes
  useEffect(() => {
    checkNowButtonDisabled();
}, [minTime, maxTime, checkNowButtonDisabled]);

  // Also check when dropdown opens
  useEffect(() => {
    if (isOpen) {
      checkNowButtonDisabled();
    }
  }, [isOpen, checkNowButtonDisabled]);

  // Sync state when value prop changes
  useEffect(() => {
    if (value && !isTyping) {
      const parsed = parseTimeString(format(value, 'hh:mm aa'));
      if (parsed) {
        setHours(parsed.hours);
        setMinutes(parsed.minutes);
        setPeriod(parsed.period);
        setInputValue(formatTime(parsed.hours, parsed.minutes, parsed.period));
      }
    }
  }, [value, isTyping, parseTimeString, formatTime]);

  // Auto-correct input if out of range after minTime/maxTime changes
  useEffect(() => {
    if (inputValue && !isTyping) {
      const isValid = isValueValid(inputValue);
      if (!isValid) {
        let closestTime = null
        if (isPeriodToggle) {
          closestTime = inputValue;
          setIsPeriodToggle(false);
        } else {
          closestTime = getClosestValidTime(inputValue);
        }
        
        const parsed = parseTimeString(closestTime);
        if (parsed) {
          setHours(parsed.hours);
          setMinutes(parsed.minutes);
          setPeriod(parsed.period);
          setInputValue(formatTime(parsed.hours, parsed.minutes, parsed.period));
          if (onChange) onChange(formatTime(parsed.hours, parsed.minutes, parsed.period));
        }
      }
    }
  }, [minTime, maxTime, inputValue, isTyping, isValueValid, getClosestValidTime, parseTimeString, formatTime, onChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        if (isTyping && inputValue && inputValue.length > 0) {
          handleInputBlur();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputValue, isTyping]);

  // Recalculate dropdown position on open/scroll
  useEffect(() => {
    if (isOpen) {
      checkDropdownPosition();
      const handleScroll = () => checkDropdownPosition();
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [isOpen, checkDropdownPosition]);

  // Memoized input change handler to avoid unnecessary re-renders
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    setIsTyping(true);
    if (/^\d{1,2}:\d{2}\s*(AM|PM|am|pm)$/i.test(value)) {
      const parsed = parseTimeString(value);
      if (parsed) {
        setHours(parsed.hours);
        setMinutes(parsed.minutes);
        setPeriod(parsed.period);
      }
    }
  }, [parseTimeString]);

  // Memoized keydown handler for input validation
  const handleInputKeyDown = useCallback((e) => {
    // Allow paste events to go through, only restrict manual key input
    if (e.ctrlKey && (e.key === 'v' || e.key === 'V')) return;
    if (e.metaKey && (e.key === 'v' || e.key === 'V')) return;

    const cursorPos = e.target.selectionStart;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Escape", "Enter"].includes(e.key)) return;

    // Only allow valid chars at each position:
    // 0-1: digits (hour)
    // 2: colon
    // 3-4: digits (minute)
    // 5: space
    // 6-7: a/A/p/P/m/M (period)
    if (cursorPos === 0 || cursorPos === 1) {
      if (!/\d/.test(e.key)) e.preventDefault();
      return;
    }
    if (cursorPos === 2) {
      if (e.key !== ':') e.preventDefault();
      return;
    }
    if (cursorPos === 3 || cursorPos === 4) {
      if (!/\d/.test(e.key)) e.preventDefault();
      return;
    }
    if (cursorPos === 5) {
      if (e.key !== ' ') e.preventDefault();
      return;
    }
    if (cursorPos === 6 || cursorPos === 7) {
      if (!/[APMapm]/.test(e.key)) e.preventDefault();
      return;
    }
    // Prevent any other input beyond 8 chars
    if (cursorPos > 7) {
      e.preventDefault();
      return;
    }
  }, []);

  // Adjust selection when user selects text in the input
  const handleMouseUp = useCallback((e) => {
    // Prevent users from selecting the ":" or space separator in the time input.
    // Adjusts the selection range if the user tries to select across the separator,
    // ensuring only valid time parts (hours, minutes, period) are selected.
    const input = e.target;
    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;

    if (selectionStart !== selectionEnd) {
      const value = input.value;
      let adjustedStart = selectionStart;
      let adjustedEnd = selectionEnd;

      // If selection starts exactly at the separator, move start to after the separator
      if (selectionStart === 5) adjustedStart = 6;
      // If selection ends exactly at the separator, move end to before the separator
      if (selectionStart < 6 && selectionEnd === 6) adjustedEnd = 5;
      // If selection spans across the separator, restrict selection to before separator
      if (selectionStart < 6 && selectionEnd > 6 && value[5] === ' ') {
        input.setSelectionRange(selectionStart, 5);
      } else if (adjustedStart !== selectionStart || adjustedEnd !== selectionEnd) {
        input.setSelectionRange(adjustedStart, adjustedEnd);
      }
    }
  }, []);

  // Auto-correct and format input value on blur
  const smartFixInput = useCallback((value) => {
    let raw = value.trim();
    // If already in HH:MM AM/PM format, just normalize spacing/case
    const timeFormatRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)$/i;
    const match = raw.match(timeFormatRegex);
    if (match) {
      let hourNum = parseInt(match[1], 10);
      let minuteNum = parseInt(match[2], 10);
      let period = match[3].toUpperCase();
      if (hourNum < 1) hourNum = 1;
      if (hourNum > 12) hourNum = 12;
      if (minuteNum < 0) minuteNum = 0;
      if (minuteNum > 59) minuteNum = 59;
      return `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')} ${period}`;
    }

    let result = raw.toUpperCase().replace(/[^0-9APM: ]/gi, '');

    // Handle 6-digit input like 145500, 455612
    if (/^\d{6}$/.test(result)) {
      let hourNum = parseInt(result.slice(0, 2), 10);
      let minuteNum = parseInt(result.slice(2, 4), 10);
      let period = 'AM';
      if (hourNum === 0) {
        hourNum = 12;
        period = 'AM';
      } else if (hourNum === 12) {
        period = 'PM';
      } else if (hourNum > 12) {
        period = 'PM';
        hourNum = hourNum - 12;
      }
      return `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')} ${period}`;
    }

    // Handle 4-digit input like 0225 (HHMM)
    if (/^\d{4}$/.test(result)) {
      let hourNum = parseInt(result.slice(0, 2), 10);
      let minuteNum = parseInt(result.slice(2, 4), 10);
      let period = 'AM';
      if (hourNum === 0) {
        hourNum = 12;
        period = 'AM';
      } else if (hourNum === 12) {
        period = 'PM';
      } else if (hourNum > 12) {
        period = 'PM';
        hourNum = hourNum - 12;
      }
      return `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')} ${period}`;
    }

    // Handle 4-digit+period input like 0225ap, 1130pm, 0930AM
    if (/^(\d{3,4})([AP][M]?)$/i.test(result.replace(/\s/g, ''))) {
      const match2 = result.replace(/\s/g, '').match(/^(\d{3,4})([AP][M]?)$/i);
      let num = match2[1];
      let hourNum, minuteNum;
      if (num.length === 3) {
        hourNum = parseInt(num.slice(0, 1), 10);
        minuteNum = parseInt(num.slice(1, 3), 10);
      } else {
        hourNum = parseInt(num.slice(0, 2), 10);
        minuteNum = parseInt(num.slice(2, 4), 10);
      }
      let period = match2[2].toUpperCase();
      if (period.length === 1) period = period + 'M';
      if (hourNum === 0) hourNum = 12;
      if (hourNum > 12) hourNum = 12;
      if (minuteNum > 59) minuteNum = 59;
      if (minuteNum < 0) minuteNum = 0;
      return `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')} ${period}`;
    }

    const parts = result.split(/[: ]/).filter(Boolean);
    if (parts[0]) {
      let hourStr = parts[0].replace(/\D/g, '');
      if (hourStr) {
        let hourNum = parseInt(hourStr, 10);
        if (hourNum > 12) hourNum = 12;
        if (hourNum < 1) hourNum = 1;
        parts[0] = hourNum.toString().padStart(2, '0');
      } else {
        parts[0] = '12';
      }
    } else {
      parts[0] = '12';
    }
    if (parts.length > 1) {
      let minuteStr = parts[1].replace(/\D/g, '');
      if (minuteStr) {
        let minuteNum = parseInt(minuteStr, 10);
        if (minuteNum > 59) minuteNum = 59;
        if (minuteNum < 0) minuteNum = 0;
        parts[1] = minuteNum.toString().padStart(2, '0');
      } else {
        parts[1] = '00';
      }
    } else {
      parts[1] = '00';
    }
    if (parts.length > 2) {
      let periodStr = parts[2].replace(/[^APM]/gi, '').toUpperCase();
      if (periodStr.startsWith('A')) {
        periodStr = 'AM';
      } else if (periodStr.startsWith('P')) {
        periodStr = 'PM';
      } else {
        periodStr = 'AM';
      }
      parts[2] = periodStr;
    } else {
      parts[2] = 'AM';
    }
    return `${parts[0]}:${parts[1]} ${parts[2]}`;
  }, []);

  // Handle input blur event, auto-correct and update state
  const handleInputBlur = useCallback(() => {
    if (!inputValue.trim()) {
      setInputValue('');
      if (onChange) onChange('');
      setIsTyping(false);
      return;
    }
    let finalValue = inputValue.trim();
    finalValue = smartFixInput(finalValue);
    const parsed = parseTimeString(finalValue);
    if (parsed) {
      const isValid = isValueValid(finalValue);
      if (!isValid) {
        const closestTime = getClosestValidTime(finalValue);
        const closestParsed = parseTimeString(closestTime);
        if (closestParsed) {
          setHours(closestParsed.hours);
          setMinutes(closestParsed.minutes);
          setPeriod(closestParsed.period);
          const formatted = formatTime(closestParsed.hours, closestParsed.minutes, closestParsed.period);
          setInputValue(formatted);
          if (onChange) onChange(formatted);
          setIsTyping(false);
          return;
        }
      }
      const formatted = formatTime(parsed.hours, parsed.minutes, parsed.period);
      setInputValue(formatted);
      setHours(parsed.hours);
      setMinutes(parsed.minutes);
      setPeriod(parsed.period);
      if (onChange) onChange(formatted);
    } else {
      const lastValid = formatTime(hours, minutes, period);
      setInputValue(lastValid);
      if (onChange) onChange(lastValid);
    }
    setIsTyping(false);
  }, [inputValue, onChange, smartFixInput, parseTimeString, isValueValid, getClosestValidTime, formatTime, hours, minutes, period]);

  // Memoized handler to toggle dropdown open/close
  const handleIconClick = useCallback(() => {
    checkDropdownPosition();
    setIsOpen(prev => {
      const next = !prev;
      if (next && isTyping && inputValue && inputValue.length > 0) {
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      }
      return next;
    });
  }, [checkDropdownPosition, isTyping, inputValue]);

  // Memoized handler for hour selection
  const handleHourChange = useCallback((hour) => {
    const newHours = hour.padStart(2, '0');
    setHours(newHours);
    const enabledMinutes = getFilteredMinuteOptions();
    let newMinutes = minutes;
    if (enabledMinutes.length > 0 && !enabledMinutes.includes(minutes)) {
      newMinutes = enabledMinutes[0];
    }
    const newTime = formatTime(newHours, newMinutes, period);
    setInputValue(newTime);
    setMinutes(newMinutes);
    if (onChange) onChange(newTime);
    setIsTyping(false);
  }, [getFilteredMinuteOptions, minutes, formatTime, period, onChange]);

  // Memoized handler for minute selection
  const handleMinuteChange = useCallback((minute) => {
    setMinutes(minute);
    const newTime = formatTime(hours, minute, period);
    setInputValue(newTime);
    if (onChange) onChange(newTime);
    setIsTyping(false);
    setIsOpen(false);
  }, [formatTime, hours, period, onChange]);

  // Memoized handler to toggle AM/PM period
  const togglePeriod = useCallback(() => {
    setIsPeriodToggle(true);
    const newPeriod = period === 'AM' ? 'PM' : 'AM';
    setPeriod(newPeriod);
    // const enabledMinutes = minuteOptions.filter(minute => !isOptionDisabled(hours, minute, newPeriod));
    // let newMinutes = minutes;
    // if (enabledMinutes.length > 0 && !enabledMinutes.includes(minutes)) {
    //   newMinutes = enabledMinutes[0];
    // }
    const newTime = formatTime(hours, minutes, newPeriod);
    setInputValue(newTime);
    setMinutes(minutes);
    if (onChange) onChange(newTime);
    setIsTyping(false);
  }, [minuteOptions, isOptionDisabled, hours, minutes, period, formatTime, onChange]);

  // Set picker to current time (rounded to nearest 15 minutes)
  const setCurrentTime = () => {
    // Check if Now button is disabled before setting time
    if (isNowDisabled) return;

    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    let hours12 = currentHours % 12;
    if (hours12 === 0) hours12 = 12;
    const period = currentHours >= 12 ? 'PM' : 'AM';
    const newTime = formatTime(
      hours12.toString().padStart(2, '0'),
      currentMinutes.toString().padStart(2, '0'),
      period
    );
    const isValid = isValueValid(newTime);
    if (!isValid) {
      const closestTime = getClosestValidTime(newTime);
      const parsed = parseTimeString(closestTime);
      if (parsed) {
        setInputValue(formatTime(parsed.hours, parsed.minutes, parsed.period));
        setHours(parsed.hours);
        setMinutes(parsed.minutes);
        setPeriod(parsed.period);
        if (onChange) onChange(formatTime(parsed.hours, parsed.minutes, parsed.period));
        setIsOpen(false);
        return;
      }
    }
    setInputValue(newTime);
    setHours(hours12.toString().padStart(2, '0'));
    setMinutes(currentMinutes.toString().padStart(2, '0'));
    setPeriod(period);
    if (onChange) onChange(newTime);
    setIsOpen(false);
  };

  // Memoized filtered hour and minute options
  const filteredHourOptions = useMemo(getFilteredHourOptions, [getFilteredHourOptions]);
  const filteredMinuteOptions = useMemo(getFilteredMinuteOptions, [getFilteredMinuteOptions]);

  const hoursVerticalColumnsClass = layout === 'vertical-columns' && isVerticalSingleColumn ? '' : 'vertical-options-hours';
  const minutesVerticalColumnsClass = layout === 'vertical-columns' && isVerticalSingleColumn ? '' : `vertical-options-minutes${minuteOptions.length <= 6 ? '-less-than-6' : ''}`;

  return (
    <div className={`time-picker-wrapper`} ref={wrapperRef}>
      <div className={`time-input-container ${className}`}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          onMouseUp={handleMouseUp}
          placeholder={placeholder}
          disabled={disabled}
          className="form-control time-input"
          aria-label="Time input"
          onClick={(e) => e.stopPropagation()}
        />
        <button
          type="button"
          className="time-picker-toggle"
          onClick={handleIconClick}
          disabled={disabled}
          aria-label="Toggle time picker"
          aria-expanded={isOpen}
        >
          <img src={timeicon} alt="time icon" />
        </button>
      </div>
      {commonFunctions.InvalidFeedbackString(valid, label)}

      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className={`time-picker-dropdown ${layout === 'vertical-columns' ? 'vertical-columns-layout' : ''} ${
            dropdownPosition === 'top' ? 'time-picker-dropdown-top' : ''
          }`}
          role="dialog"
          aria-label="Time picker"
        >
          {layout === 'vertical-columns' ? (
            // Vertical three column layout
            <div className="vertical-columns-container">
              <div className="vertical-column">
                <div className="column-header" title={TEXT.labelHours}>{TEXT.labelHours}</div>
                <div className={`vertical-options ${hoursVerticalColumnsClass}`}>
                  {hourOptions.map((hour) => {
                    const isDisabled = isHourDisabled(hour);
                    return (
                      <button
                        key={hour}
                        type="button"
                        className={`vertical-option hour-option ${hours === hour ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => !isDisabled && handleHourChange(hour)}
                        disabled={isDisabled}
                        aria-label={`${parseInt(hour, 10)} hour`}
                        aria-pressed={hours === hour}
                        aria-disabled={isDisabled}
                      >
                        {hour}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="vertical-column">
                <div className="column-header" title={TEXT.labelMinutes}>{TEXT.labelMinutes}</div>
                <div className={`vertical-options ${minutesVerticalColumnsClass}`}>
                  {minuteOptions.map((minute) => {
                    const isDisabled = isOptionDisabled(hours, minute, period);
                    return (
                      <button
                        key={minute}
                        type="button"
                        className={`vertical-option minute-option ${minutes === minute ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => !isDisabled && handleMinuteChange(minute)}
                        disabled={isDisabled}
                        aria-label={`${minute} minute`}
                        aria-pressed={minutes === minute}
                        aria-disabled={isDisabled}
                      >
                        {minute}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="vertical-column">
                <div className="column-header" title={TEXT.labelPeriod}>{TEXT.labelPeriod}</div>
                <div className="vertical-options">
                  <div className={`period-toggle vertical-period-toggle ${isPeriodDisabled('AM') && isPeriodDisabled('PM') ? 'disabled' : ''}`}>
                    <button
                      type="button"
                      className={`period-toggle-option ${period === 'AM' ? 'selected' : ''} ${isPeriodDisabled('AM') ? 'disabled' : ''}`}
                      onClick={() => !isPeriodDisabled('AM') && togglePeriod()}
                      disabled={isPeriodDisabled('AM')}
                      aria-label="AM"
                      aria-pressed={period === 'AM'}
                      aria-disabled={isPeriodDisabled('AM')}
                    >
                      {TEXT.labelAM}
                    </button>
                    <button
                      type="button"
                      className={`period-toggle-option ${period === 'PM' ? 'selected' : ''} ${isPeriodDisabled('PM') ? 'disabled' : ''}`}
                      onClick={() => !isPeriodDisabled('PM') && togglePeriod()}
                      disabled={isPeriodDisabled('PM')}
                      aria-label="PM"
                      aria-pressed={period === 'PM'}
                      aria-disabled={isPeriodDisabled('PM')}
                    >
                      {TEXT.labelPM}
                    </button>
                    <div className={`period-toggle-slider ${period === 'PM' ? 'pm-selected' : ''}`} />
                  </div>

                  <div className="vertical-now-button-container">
                    <button
                      type="button"
                      className={`time-now-button ${isNowDisabled ? 'disabled' : ''}`}
                      onClick={setCurrentTime}
                      disabled={isNowDisabled}
                      aria-label="Set current time"
                      aria-disabled={isNowDisabled}
                    >
                      {TEXT.buttonNow}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Default grid layout
            <>
              <div className="time-picker-section">
                <div className="time-section-header" title={TEXT.labelHoursRemark}>
                  {TEXT.labelHours}
                  {(minTime || maxTime) && (
                    <span className="time-range-info">
                      {minTime && `${TEXT.prefixMin} ${format(minTime, 'hh:mm aa')}`}
                      {minTime && maxTime && ' | '}
                      {maxTime && `${TEXT.prefixMax} ${format(maxTime, 'hh:mm aa')}`}
                    </span>
                  )}
                </div>
                <div className="hour-grid">
                  {hourOptions.map((hour) => {
                    const isDisabled = isHourDisabled(hour);
                    return (
                      <button
                        key={hour}
                        type="button"
                        className={`hour-option ${hours === hour ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => !isDisabled && handleHourChange(hour)}
                        disabled={isDisabled}
                        aria-label={`${parseInt(hour, 10)} hour`}
                        aria-pressed={hours === hour}
                        aria-disabled={isDisabled}
                      >
                        {hour}
                        {isDisabled && <span className="disabled-overlay" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="time-picker-section">
                <div className="time-section-header" title={TEXT.labelMinutes}>{TEXT.labelMinutes}</div>
                <div className="minute-grid">
                  {minuteOptions.map((minute) => {
                    const isDisabled = isOptionDisabled(hours, minute, period);
                    return (
                      <button
                        key={minute}
                        type="button"
                        className={`minute-option ${minutes === minute ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => !isDisabled && handleMinuteChange(minute)}
                        disabled={isDisabled}
                        aria-label={`${minute} minute`}
                        aria-pressed={minutes === minute}
                        aria-disabled={isDisabled}
                      >
                        {minute}
                        {isDisabled && <span className="disabled-overlay" />}
                      </button>
                    );
                  })}
                </div>
                {!minuteOptions.includes(minutes) && minutes !== '' && (
                  <div className="custom-minute-note">
                    {TEXT.noteCustomMinute.replace('{minutes}', minutes)}
                  </div>
                )}
              </div>

              <div className="time-picker-section">
                <div className="time-section-header" title={TEXT.labelPeriod}>{TEXT.labelPeriod}</div>
                <div className="period-toggle-wrapper">
                  <div className={`period-toggle ${isPeriodDisabled('AM') && isPeriodDisabled('PM') ? 'disabled' : ''}`}>
                    <button
                      type="button"
                      className={`period-toggle-option ${period === 'AM' ? 'selected' : ''} ${isPeriodDisabled('AM') ? 'disabled' : ''}`}
                      onClick={() => !isPeriodDisabled('AM') && togglePeriod()}
                      disabled={isPeriodDisabled('AM')}
                      aria-label="AM"
                      aria-pressed={period === 'AM'}
                      aria-disabled={isPeriodDisabled('AM')}
                    >
                      {TEXT.labelAM}
                    </button>
                    <button
                      type="button"
                      className={`period-toggle-option ${period === 'PM' ? 'selected' : ''} ${isPeriodDisabled('PM') ? 'disabled' : ''}`}
                      onClick={() => !isPeriodDisabled('PM') && togglePeriod()}
                      disabled={isPeriodDisabled('PM')}
                      aria-label="PM"
                      aria-pressed={period === 'PM'}
                      aria-disabled={isPeriodDisabled('PM')}
                    >
                      {TEXT.labelPM}
                    </button>
                    <div className={`period-toggle-slider ${period === 'PM' ? 'pm-selected' : ''}`} />
                  </div>
                </div>
              </div>

              <div className="time-picker-footer">
                <button
                  type="button"
                  className={`time-now-button ${isNowDisabled ? 'disabled' : ''}`}
                  onClick={setCurrentTime}
                  disabled={isNowDisabled}
                  aria-label="Set current time"
                  aria-disabled={isNowDisabled}
                >
                  {TEXT.buttonNow}
                  {isNowDisabled && (
                    <span className="now-disabled-tooltip">
                      {`${TEXT.tooltipNowDisabled} (${maxTime ? format(maxTime, 'hh:mm aa') : ''})`}
                    </span>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TimePicker;